// Типы для серверного API чатов и сообщений

export enum MessageSender {
  USER = 'user',
  AI = 'ai'
}

export enum MessageType {
  TEXT = 'text',
  JSON = 'json',
  PHOTO = 'photo',
  AUDIO = 'audio'
}

// Серверные типы (соответствуют API документации)
export interface ServerChat {
  id: number;
  title: string;
  description?: string;
  user_id: number;
  is_active: boolean;
  created_at: string; // ISO datetime
  updated_at: string; // ISO datetime
}

export interface ServerMessage {
  id: number;
  sender: MessageSender;
  time: string; // HH:MM format
  type: MessageType;
  text: string;
  json_content?: Record<string, any>; // JSON content for AI messages (сервер использует json_content)
  title?: string; // Title for AI messages
  specialty?: string; // Specialty for user messages
  chat_id: number;
  created_at: string; // ISO datetime
  updated_at: string; // ISO datetime
}

export interface ServerChatWithMessages extends ServerChat {
  messages: ServerMessage[];
}

export interface PaginatedChatsResponse {
  items: ServerChat[];
  total: number;
  skip: number;
  limit: number;
  has_next: boolean;
}

// Типы для создания/обновления
export interface ChatCreate {
  title: string; // 1-200 символов
  description?: string; // до 1000 символов
}

export interface ChatUpdate {
  title?: string; // 1-200 символов
  description?: string; // до 1000 символов
  is_active?: boolean;
}

export interface UserMessageCreate {
  sender: MessageSender; // default: "user"
  time: string; // HH:MM format
  type: MessageType; // default: "text"
  text: string;
  specialty: string; // Специальность врача
  chat_id: number;
}

export interface AIMessageCreate {
  sender: MessageSender; // default: "ai"
  time: string; // HH:MM format
  type: MessageType; // default: "json"
  text: string;
  json_content?: Record<string, any>;
  title?: string;
  chat_id: number;
}

export interface MessageUpdate {
  text?: string;
  json_content?: Record<string, any>;
  title?: string;
}

// Типы для потоковых сообщений
export interface StreamMessageChunk {
  event: 'new_message' | 'heartbeat' | 'error';
  data: ServerMessage | string;
}

// Локальные типы (для совместимости с существующим кодом)
export interface LocalChat {
  id: string;
  title: string;
  lastMessageTime: string;
  messages: LocalMessage[];
  createdAt: string;
  serverId?: number; // ID на сервере, если синхронизирован
  isDirty?: boolean; // Есть ли несинхронизированные изменения
}

export interface LocalMessage {
  sender: 'user' | 'ai';
  time: string;
  type: 'text' | 'photo' | 'audio' | 'json';
  text?: string;
  title?: string;
  transcription?: string;
  specialty?: string;
  speakerId?: string;
  json?: any;
  serverId?: number; // ID на сервере, если синхронизирован
  isDirty?: boolean; // Есть ли несинхронизированные изменения
}

// Утилиты для конвертации между серверными и локальными типами
export const serverChatToLocal = (serverChat: ServerChat, messages: LocalMessage[] = []): LocalChat => ({
  id: serverChat.id.toString(),
  title: serverChat.title,
  lastMessageTime: serverChat.updated_at, // Сохраняем в ISO формате
  messages,
  createdAt: serverChat.created_at,
  serverId: serverChat.id,
  isDirty: false
});

export const serverMessageToLocal = (serverMessage: ServerMessage): LocalMessage => {
  const rawTime = (serverMessage as any).created_at || (serverMessage as any).updated_at || serverMessage.time;
  const normalizedTime =
    typeof rawTime === 'string' && rawTime && !rawTime.endsWith('Z') && !rawTime.includes('+')
      ? `${rawTime}Z`
      : rawTime;

  return {
    sender: serverMessage.sender,
    time: normalizedTime,
    type: serverMessage.type,
    text: serverMessage.text,
    title: serverMessage.title,
    specialty: serverMessage.specialty,
    json: serverMessage.json_content, // Используем json_content с сервера
    serverId: serverMessage.id,
    isDirty: false
  };
};

export const localMessageToServer = (localMessage: LocalMessage, chatId: number): UserMessageCreate | AIMessageCreate => {
  const baseMessage = {
    sender: localMessage.sender as MessageSender,
    time: localMessage.time,
    type: localMessage.type as MessageType,
    text: localMessage.text || '',
    chat_id: chatId
  };

  if (localMessage.sender === 'user') {
    return {
      ...baseMessage,
      specialty: localMessage.specialty || ''
    } as UserMessageCreate;
  } else {
    return {
      ...baseMessage,
      json_content: localMessage.json,
      title: localMessage.title
    } as AIMessageCreate;
  }
};
